/**
 * Auth Provider with React Query
 * 
 * Provides React Query client and handles authentication state
 * with automatic token validation and persistence.
 */

import React from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { queryClient } from '../../lib/queryClient'
import { useAuth } from '../../hooks/useAuth'
import { logger } from '../../utils/logger'

interface AuthProviderProps {
  children: React.ReactNode
}

/**
 * Inner component that uses auth hooks
 */
const AuthInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoading, user, isAuthenticated } = useAuth()

  // Log auth state for debugging - ALWAYS call this hook
  React.useEffect(() => {
    logger.debug('Auth state updated', {
      component: 'AuthInitializer',
      isAuthenticated,
      userId: user?.id || 'none'
    })
  }, [isAuthenticated, user])

  // Show loading state during auth initialization
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Initializing...</p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

/**
 * AuthProvider Component
 * 
 * Provides React Query and authentication context
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthInitializer>
        {children}
      </AuthInitializer>
      
      {/* React Query DevTools - only in development */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  )
}

export default AuthProvider